<template>
  <section class="course-questions" :class="{ 'course-questions--embedded': embedded, 'is-open': open }">
    <button v-if="embedded" type="button" class="course-questions__bar" @click="open = !open">
      <span>向作者提问</span>
      <em v-if="questions.length">{{ questions.length }}</em>
      <span class="course-questions__chevron">{{ open ? '收起' : '展开' }}</span>
    </button>
    <h3 v-else>向作者提问</h3>
    <div v-show="!embedded || open" class="course-questions__body">
      <p class="hint">卡在视频或知识点上时写给作者。提交后只有你和作者能看到，作者通过后才会公开。</p>
      <form v-if="canAsk" class="ask" @submit.prevent="ask">
        <el-input v-model="draft" type="textarea" :rows="3" maxlength="500" placeholder="写出你卡住的地方，2 到 500 字" />
        <el-button type="primary" native-type="submit" :loading="sending" :disabled="draft.trim().length < 2">提交问题</el-button>
      </form>
      <p v-else-if="!loggedIn" class="hint">登录并开始学习后可以提问。</p>
      <p v-else class="hint">购买或兑换这门课后可以提问。</p>

      <div v-for="item in questions" :key="item.id" class="qa">
        <p class="q">
          <strong>{{ item.nickname || '学员' }}</strong>：{{ item.content }}
          <span v-if="item.auditStatus === 0" class="wait">待审核</span>
          <span v-else-if="item.auditStatus === 2" class="wait">未通过</span>
        </p>
        <p v-if="item.answer" class="a">作者：{{ item.answer }}</p>
        <form v-else-if="canAnswer" class="ask" @submit.prevent="answer(item)">
          <el-input v-model="replies[item.id]" type="textarea" :rows="2" maxlength="1000" placeholder="写给这位学员的回答。回复后这条提问会公开" />
          <el-button type="primary" native-type="submit" :loading="answeringId === item.id">回复并公开</el-button>
        </form>
        <p v-else-if="item.auditStatus === 1" class="wait">作者还没回复</p>
        <p v-else-if="item.auditStatus === 2" class="wait">未通过{{ item.auditMessage ? '：' + item.auditMessage : '' }}。公开页不显示。</p>
        <p v-else class="wait">等待作者审核，通过后才会公开</p>
        <div v-if="canAnswer && item.auditStatus !== 1" class="review">
          <el-button size="small" type="success" plain :loading="reviewingId === item.id" @click="review(item, 1)">通过</el-button>
          <el-button v-if="item.auditStatus !== 2" size="small" type="danger" plain :loading="reviewingId === item.id" @click="review(item, 2)">驳回</el-button>
        </div>
      </div>
      <p v-if="!loading && questions.length === 0" class="hint">还没有提问。</p>
    </div>
  </section>
</template>

<script>
import axios from 'axios'
import { ElMessage } from 'element-plus'
import { authHeaders } from '@/utils/http'
import { promptRejectReason } from '@/utils/promptRejectReason'

export default {
  name: 'CourseQuestions',
  props: {
    kcId: { type: Number, required: true },
    embedded: { type: Boolean, default: false }
  },
  data() {
    return {
      open: false,
      questions: [],
      canAsk: false,
      canAnswer: false,
      draft: '',
      replies: {},
      sending: false,
      answeringId: 0,
      reviewingId: 0,
      loading: false
    }
  },
  computed: {
    loggedIn() {
      return !!localStorage.getItem('jwt_token')
    }
  },
  watch: {
    kcId: {
      immediate: true,
      handler() {
        this.load()
      }
    }
  },
  methods: {
    async load() {
      if (!this.kcId) return
      this.loading = true
      try {
        const headers = this.loggedIn ? authHeaders() : {}
        const res = await axios.post('/api/v1/knowledge/course/question/list', { kcId: this.kcId }, { headers, skipAuth: !this.loggedIn })
        if (res.data.code === 200) {
          this.questions = res.data.data?.questions || []
          this.canAsk = !!res.data.data?.canAsk
          this.canAnswer = !!res.data.data?.canAnswer
        }
      } catch (e) {
        this.questions = []
      } finally {
        this.loading = false
      }
    },
    async ask() {
      const content = this.draft.trim()
      if (content.length < 2) return
      this.sending = true
      try {
        const res = await axios.post('/api/v1/knowledge/course/question/ask', { kcId: this.kcId, content }, { headers: authHeaders() })
        if (res.data.code === 200) {
          this.draft = ''
          ElMessage.success(res.data.message || '已提交')
          await this.load()
        } else {
          ElMessage.error(res.data.message || '提交失败')
        }
      } catch (e) {
        ElMessage.error('提交失败')
      } finally {
        this.sending = false
      }
    },
    async review(item, auditStatus) {
      let auditMessage = ''
      if (auditStatus === 2) {
        try {
          auditMessage = await promptRejectReason({
            title: '不公开这条提问',
            message: '驳回后只有提问人和你能看到。可点击常见原因加入下方。',
            category: 'comment',
            confirmText: '驳回'
          })
        } catch (e) {
          return
        }
      }
      this.reviewingId = item.id
      try {
        const res = await axios.post('/api/v1/knowledge/course/question/audit', {
          questionId: item.id,
          auditStatus,
          auditMessage
        }, { headers: authHeaders() })
        if (res.data.code === 200) {
          ElMessage.success(res.data.message || '已保存')
          await this.load()
        } else {
          ElMessage.error(res.data.message || '审核失败')
        }
      } catch (e) {
        ElMessage.error('审核失败')
      } finally {
        this.reviewingId = 0
      }
    },
    async answer(item) {
      const answer = (this.replies[item.id] || '').trim()
      if (!answer) return
      this.answeringId = item.id
      try {
        const res = await axios.post('/api/v1/knowledge/course/question/answer', { questionId: item.id, answer }, { headers: authHeaders() })
        if (res.data.code === 200) {
          ElMessage.success(res.data.message || '已回复')
          await this.load()
        } else {
          ElMessage.error(res.data.message || '回复失败')
        }
      } catch (e) {
        ElMessage.error('回复失败')
      } finally {
        this.answeringId = 0
      }
    }
  }
}
</script>

<style scoped>
.course-questions { margin: 24px 16px 40px; padding: 16px; background: #fff; border-radius: 12px; }
.course-questions h3 { margin: 0 0 8px; }
.course-questions--embedded {
  margin: 12px 0 0;
  padding: 0;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e6e8eb;
  flex-shrink: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.course-questions--embedded.is-open {
  max-height: 42%;
}
.course-questions--embedded.is-open .course-questions__body {
  flex: 1;
  overflow-y: auto;
}
.course-questions__bar {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  flex-shrink: 0;
  border: none;
  background: #f7f8fa;
  color: #303133;
  font-size: 14px;
  font-weight: 600;
  text-align: left;
  padding: 10px 12px;
  cursor: pointer;
}
.course-questions__bar em {
  font-style: normal;
  font-size: 12px;
  font-weight: 500;
  color: #409eff;
  background: #ecf5ff;
  border-radius: 10px;
  padding: 0 6px;
  line-height: 18px;
}
.course-questions__chevron {
  margin-left: auto;
  font-size: 12px;
  font-weight: 500;
  color: #909399;
}
.course-questions__body {
  min-height: 0;
  overflow-y: auto;
}
.course-questions--embedded .course-questions__body {
  padding: 8px 12px 12px;
}
.hint, .wait { color: var(--el-text-color-secondary); font-size: 13px; }
.ask, .review { display: flex; flex-direction: column; align-items: flex-start; gap: 8px; margin: 12px 0; }
.review { flex-direction: row; }
.qa { padding: 12px 0; border-top: 1px solid var(--el-border-color-lighter); }
.q, .a { margin: 0 0 6px; white-space: pre-wrap; word-break: break-word; }
.a { color: var(--el-text-color-regular); }
</style>
